import { TZDate } from '@date-fns/tz';
import { format, isValid, parseISO } from 'date-fns';
import { printTimezones } from 'src/app/constants/timezones';

/** Mirrors moment(value).isValid(): strict ISO parsing, native Date fallback otherwise. */
export function isValidDateTimeInput(value: string): boolean {
  return /^\s*\d{4}-\d{2}/.test(value)
    ? isValid(parseISO(value.trim()))
    : isValid(new Date(value));
}

/** Parse an instant, treating ISO date-times without an explicit zone as UTC. */
export function toUtcDate(value: Date | string): Date {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  const normalized = value.trim().replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
  if (!/[T ]\d{1,2}:\d{2}/.test(normalized)) {
    return new Date(normalized);
  }

  const zoneNormalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const hasTimezone = /[zZ]$|[+-]\d{2}(?::\d{2})?$/.test(zoneNormalized);
  return new Date(hasTimezone ? zoneNormalized : `${zoneNormalized}Z`);
}

/** Parse a date using local-time semantics, including date-only ISO strings. */
export function toLocalDate(value: Date | string): Date {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : new Date(value);
}

/** Interpret the local date/time fields of a Date as wall-clock time in a zone. */
export function wallClockTimeInTimeZone(
  date: Date | null | undefined,
  timeZone: string
): Date {
  if (!date || !isValid(date)) {
    return new Date(Number.NaN);
  }

  const zonedDate = TZDate.tz(
    timeZone,
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  );
  return new Date(zonedDate.getTime());
}

/** Return an instant as a local Date containing the target zone's wall-clock fields. */
export function zonedInstantAsLocalDate(
  instant: Date | string,
  timeZone: string,
  includeSeconds = true
): Date {
  const zonedDate = TZDate.tz(timeZone, toUtcDate(instant).getTime());
  return new Date(
    zonedDate.getFullYear(),
    zonedDate.getMonth(),
    zonedDate.getDate(),
    zonedDate.getHours(),
    zonedDate.getMinutes(),
    includeSeconds ? zonedDate.getSeconds() : 0
  );
}

export function getDateAsUtc(d: Date | string): Date {
  return toUtcDate(d);
}

export function getFirstDayOfMonth(date: Date): Date {
  const retVal = new Date(date);
  retVal.setDate(1);
  return retVal;
}

export function getLogIdFromDate() {
  return format(new Date(), 'yyyyMMdd-HHmmssSSS');
}

export function getUserTimezone() {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let match = printTimezones.find((tz) => tz.value === userTimezone);

  if (!match) {
    let offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const sign = offsetMinutes > 0 ? '-' : '+';
    offsetMinutes = Math.abs(offsetMinutes) % 60;

    // Format the offset as UTC +/-HH:MM
    return `UTC${sign}${String(offsetHours).padStart(2, '0')}:${String(
      offsetMinutes
    ).padStart(2, '0')}`;
  }

  return match.label;
}
